import React, { useState } from 'react';
import { X, Briefcase, User, MapPin, Hash, FileText } from 'lucide-react';
import { CaseMetadata } from '../types';

interface NewBoardDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (title: string, metadata: CaseMetadata) => void;
    investigatorName?: string;
}

const NewBoardDialog: React.FC<NewBoardDialogProps> = ({
    isOpen,
    onClose,
    onCreate,
    investigatorName
}) => {
    const [title, setTitle] = useState('');
    const [metadata, setMetadata] = useState<CaseMetadata>({
        caseNumber: '',
        caseType: 'other',
        victimName: '',
        suspectName: '',
        incidentLocation: '',
        investigatorName: investigatorName || '',
        status: 'active'
    });

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) return;
        onCreate(title, metadata);
        onClose();
    };

    const inputClass = "w-full bg-slate-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-700 text-gray-900 dark:text-gray-200 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2";
    const labelClass = "block text-xs font-medium text-gray-400 mb-1";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <Briefcase size={20} className="text-indigo-500" />
                        New Investigation
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {/* Main Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label htmlFor="title" className={labelClass}>Investigation Title *</label>
                            <input
                                id="title"
                                type="text"
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. Operation Nightfall"
                                className={inputClass}
                                autoFocus
                            />
                        </div>

                        <div>
                            <label htmlFor="caseNumber" className={labelClass}>Case Number</label>
                            <div className="relative">
                                <Hash size={14} className="absolute left-2.5 top-3 text-gray-500" />
                                <input
                                    id="caseNumber"
                                    type="text"
                                    value={metadata.caseNumber}
                                    onChange={(e) => setMetadata({ ...metadata, caseNumber: e.target.value })}
                                    placeholder="202X-001"
                                    className={`${inputClass} pl-8`}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="caseType" className={labelClass}>Case Type</label>
                            <select
                                id="caseType"
                                value={metadata.caseType}
                                onChange={(e) => setMetadata({ ...metadata, caseType: e.target.value as any })}
                                className={inputClass}
                            >
                                <option value="other">Other</option>
                                <option value="homicide">Homicide</option>
                                <option value="fraud">Fraud / Financial</option>
                                <option value="cyber">Cybercrime</option>
                                <option value="missing_person">Missing Person</option>
                                <option value="narcotics">Narcotics</option>
                            </select>
                        </div>
                    </div>

                    <div className="h-px bg-gray-200 dark:bg-slate-700 my-2" />

                    {/* Persons involved */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="victim" className={labelClass}>Victim Name</label>
                            <div className="relative">
                                <User size={14} className="absolute left-2.5 top-3 text-gray-500" />
                                <input
                                    id="victim"
                                    type="text"
                                    value={metadata.victimName}
                                    onChange={(e) => setMetadata({ ...metadata, victimName: e.target.value })}
                                    placeholder="Doe, John"
                                    className={`${inputClass} pl-8`}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="suspect" className={labelClass}>Suspect Name</label>
                            <div className="relative">
                                <User size={14} className="absolute left-2.5 top-3 text-red-500/70" />
                                <input
                                    id="suspect"
                                    type="text"
                                    value={metadata.suspectName}
                                    onChange={(e) => setMetadata({ ...metadata, suspectName: e.target.value })}
                                    placeholder="Smith, Agent"
                                    className={`${inputClass} pl-8`}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label htmlFor="location" className={labelClass}>Incident Location</label>
                            <div className="relative">
                                <MapPin size={14} className="absolute left-2.5 top-3 text-gray-500" />
                                <input
                                    id="location"
                                    type="text"
                                    value={metadata.incidentLocation}
                                    onChange={(e) => setMetadata({ ...metadata, incidentLocation: e.target.value })}
                                    placeholder="123 Main St, Cityville"
                                    className={`${inputClass} pl-8`}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-md transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors shadow-sm"
                        >
                            Start Investigation
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewBoardDialog;
